/**
 * Access token 自动刷新（单一入口，防并发）。
 *
 * 背景：access token 15 分钟过期（SESSION_EXPIRY），到期后请求 401。
 * 本模块统一处理 refresh，三个调用方都调它：
 *   - axios 版 utils/api.ts（聊天链路 401 拦截）
 *   - fetch 版 services/api.ts（登录/账户链路 401 拦截）
 *   - SSE streamReader.ts（流式 401 重连）
 *
 * 后端协议（无需改后端）：
 *   - 登录设 httpOnly cookie `refreshToken`（AuthService.js，sameSite:strict）
 *   - POST /api/auth/refresh（浏览器同源自动带 cookie）→ 返回 { token, user }（新 access token）
 *
 * ⚠️ 后端 refresh 轮换是【破坏性】的：每次成功 refresh 都覆写
 * session.refreshTokenHash（session.ts generateRefreshToken），旧 cookie 立即作废。
 * 同浏览器两个上下文（多标签、或 custom-ui + /manage 原生页）并发刷新时必有输家：
 * 输家 401 'Refresh token expired or not found'，但赢家的 Set-Cookie 已更新共享
 * cookie jar——稍候重试一次即可恢复（见 doRefresh 的轮换竞态重试）。
 *
 * 防并发：多个 401 同时触发时，共享同一个 refreshPromise，只发一次 refresh 请求。
 * 成功后写 localStorage + dispatch tokenUpdated（utils/api.ts 已监听 → axios 自动 setToken）。
 * 真失效（重试后仍 401/403）清登录态 + 跳 /login（带 redirect_to 回现场）。
 */
let refreshPromise: Promise<string | null> | null = null

/** 轮换竞态重试等待：给并发赢家的 Set-Cookie 留落地时间 */
const ROTATION_RACE_RETRY_MS = 600

/** 刷新 access token。多个并发调用共享同一次刷新。返回新 token，失败返回 null（并跳登录）。 */
export async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) {
    return refreshPromise
  }
  refreshPromise = doRefresh()
  try {
    return await refreshPromise
  } finally {
    refreshPromise = null
  }
}

import { API_BASE } from './apiBase'

async function fetchRefresh(): Promise<Response> {
  return fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    credentials: 'include', // 带 refreshToken cookie（同源本就带，保险显式声明）
    headers: { 'Content-Type': 'application/json' },
  })
}

async function doRefresh(): Promise<string | null> {
  try {
    let resp = await fetchRefresh()

    if (!resp.ok && (resp.status === 401 || resp.status === 403)) {
      // 401/403 = cookie 与 session.refreshTokenHash 不匹配。除真失效外还有轮换竞态
      // 败者（见文件头注释）：赢家的 Set-Cookie 已更新共享 cookie jar，稍候重试一次
      // 即可用新 cookie 恢复，避免把正在执行对话的用户踢去登录页/首页。
      await new Promise((resolve) => setTimeout(resolve, ROTATION_RACE_RETRY_MS))
      resp = await fetchRefresh()

      if (!resp.ok && (resp.status === 401 || resp.status === 403)) {
        clearAuthAndRedirect()
        handleRefreshFailed()
        return null
      }
    }

    if (!resp.ok) {
      // 其他失败（500/网络错）不清 token 不跳登录——dev vite proxy 下 cookie 链路
      // 可能不通，频繁踢用户体验差；调用方按 null 走错误气泡。
      handleRefreshFailed()
      return null
    }

    const data = await resp.json().catch(() => null)
    const token = data?.token
    if (!token) {
      // 后端在 cookie 缺失时返回 200 + 字符串 'Refresh token not provided'（AuthController.js）
      handleRefreshFailed()
      return null
    }
    localStorage.setItem('token', token)
    window.dispatchEvent(new CustomEvent('tokenUpdated', { detail: token }))
    return token
  } catch {
    handleRefreshFailed()
    return null
  }
}

/** 解析 JWT exp（不验签，仅本地判断 token 是否仍可用） */
function hasUsableToken(): boolean {
  const token = localStorage.getItem('token')
  if (!token) return false
  try {
    const payload = token.split('.')[1]
    if (!payload) return false
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=')
    const decoded = JSON.parse(window.atob(padded)) as { exp?: number }
    return typeof decoded.exp === 'number' && decoded.exp * 1000 > Date.now()
  } catch {
    return false
  }
}

function handleRefreshFailed(): void {
  // refresh 失败兜底日志（真失效的清态跳转已在 doRefresh 里处理）
  console.warn('[tokenRefresh] refresh 失败：cookie 可能未带（dev vite proxy）或网络错误')
}

/** 认证流程页面：用户本来就在登录/注册/找回密码，被 401 踢去登录页只会打断流程（清态照旧，不整页跳） */
const AUTH_PAGES = ['/login', '/register', '/forgot-password', '/oauth']

function clearAuthAndRedirect(): void {
  // 并发赢家已把新 token 写回共享 localStorage（多标签竞态）：非真失效，不清不跳，
  // 交给后续请求重试链路用新 token 继续。
  if (hasUsableToken()) {
    console.warn('[tokenRefresh] 检测到并发刷新已恢复 token，跳过登出')
    return
  }
  // 真失效（重试后仍 401/403）：清登录态 + 跳登录（带 redirect_to 便于重登后回现场）
  try {
    localStorage.removeItem('token')
    localStorage.removeItem('aie-user-storage')
  } catch {
    // ignore
  }
  const onAuthPage = AUTH_PAGES.some((p) => window.location.pathname.startsWith(p))
  if (window.location.pathname !== '/login' && !onAuthPage) {
    const current = window.location.pathname + window.location.search
    const fallback = current && current !== '/' ? current : '/'
    window.location.href = '/login?redirect_to=' + encodeURIComponent(fallback)
  }
}
